import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, IconButton, CircularProgress, Slider, Tooltip, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useAuth } from "../hooks/AuthProvider";
import { VOICE_CALL_RECORDING_URL } from "../config/urls";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

// Rendered only for calls with has_recording=true (see CallHistoryWindow.js).
// On first play (or first seek), fetches a short-lived presigned URL on
// demand rather than up front for every row (see add-call-logs-ui's
// design.md Decision 7) and caches it in a ref so replaying never
// re-fetches. `activeCallId`/`onPlay` coordinate with sibling rows so only
// one recording plays at a time (see improve-call-recording-player's
// design.md Decision 1). `durationSeconds` comes from the call's own data
// (Decision 2) so the progress bar has a total from first render, with no
// need to load audio just to learn how long the call was.
const CallRecordingPlayer = ({ callId, durationSeconds, activeCallId, onPlay }) => {
  const { getValidToken } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | loading | playing | paused | error
  const [currentTime, setCurrentTime] = useState(0);
  const [dragValue, setDragValue] = useState(null); // non-null only while the user is actively dragging
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const fetchRecordingUrl = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    const response = await axios.get(VOICE_CALL_RECORDING_URL(callId), {
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": tenant,
      },
    });
    return response.data.url;
  };

  // Lazily creates (or reuses) the underlying Audio object, wiring the
  // listeners that keep this row's own state in sync with real playback.
  const ensureAudio = async () => {
    if (!urlRef.current) {
      urlRef.current = await fetchRecordingUrl();
    }
    if (!audioRef.current) {
      const audio = new Audio(urlRef.current);
      audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
      audio.onended = () => {
        setStatus("idle");
        setCurrentTime(0);
      };
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  // Pause this row whenever a different row becomes the active player.
  useEffect(() => {
    if (activeCallId !== callId && status === "playing") {
      audioRef.current?.pause();
      setStatus("paused");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCallId]);

  // Stop playback if this row unmounts mid-play (e.g. switching caller or
  // tab) rather than leaving the Audio object playing off-screen.
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const handleToggle = async (event) => {
    event.stopPropagation();

    if (status === "playing") {
      audioRef.current?.pause();
      setStatus("paused");
      return;
    }
    if (status === "paused" && audioRef.current) {
      onPlay(callId);
      await audioRef.current.play();
      setStatus("playing");
      return;
    }

    setStatus("loading");
    try {
      const audio = await ensureAudio();
      onPlay(callId);
      await audio.play();
      setStatus("playing");
    } catch {
      // Presigned URL may have expired or failed to generate — clear it so
      // the next click fetches a fresh one instead of retrying a dead URL.
      urlRef.current = null;
      audioRef.current = null;
      setStatus("error");
    }
  };

  // Continuous while dragging (and for a plain click on the track) — kept
  // out of `currentTime` so the thumb follows the drag instead of fighting
  // the audio element's own timeupdate-driven position.
  const handleSeekChange = (_event, value) => {
    setDragValue(value);
  };

  // Fires on drag release or a track click. Seeking before anything has
  // ever played behaves like pressing play at that offset, rather than a
  // no-op.
  const handleSeekCommit = async (event, value) => {
    event?.stopPropagation?.();
    setDragValue(null);

    if (status === "idle" || status === "error") {
      setStatus("loading");
      try {
        const audio = await ensureAudio();
        audio.currentTime = value;
        onPlay(callId);
        await audio.play();
        setStatus("playing");
      } catch {
        urlRef.current = null;
        audioRef.current = null;
        setStatus("error");
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  if (status === "error") {
    return (
      <Tooltip title="Could not play recording — click to retry">
        <IconButton size="small" onClick={handleToggle}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: "#c4515b" }} />
        </IconButton>
      </Tooltip>
    );
  }

  const hasDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
  const sliderValue = dragValue ?? currentTime;

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 180 }}>
      <IconButton size="small" onClick={handleToggle} disabled={status === "loading"}>
        {status === "loading" ? (
          <CircularProgress size={16} />
        ) : status === "playing" ? (
          <PauseIcon sx={{ fontSize: 18, color: "#1565c0" }} />
        ) : (
          <PlayArrowIcon sx={{ fontSize: 18, color: "#1565c0" }} />
        )}
      </IconButton>
      <Slider
        size="small"
        min={0}
        max={hasDuration ? durationSeconds : 0}
        value={hasDuration ? sliderValue : 0}
        disabled={!hasDuration}
        onChange={handleSeekChange}
        onChangeCommitted={handleSeekCommit}
        sx={{ flex: 1, mx: 0.5 }}
      />
      <Typography
        sx={{ fontSize: "0.7rem", color: "#888", whiteSpace: "nowrap", minWidth: 72, textAlign: "right" }}
      >
        {formatTime(sliderValue)} / {hasDuration ? formatTime(durationSeconds) : "--:--"}
      </Typography>
    </Box>
  );
};

export default CallRecordingPlayer;
